<template lang='pug'>
.breadcrumbs
  .crumb(:class="breadcrumbColor")
    rogue-link.crumb-link(to="/") {{ __('Home') }}
  .crumb(v-for="link in crumbs" :key="link.url" :class="breadcrumbColor")
    span.slash /
    rogue-link.crumb-link(:to="link.url" :categoryId="link.categoryId" v-if="link.url") {{ __(link.name, 'categories') }}
    span(v-else) {{ link.name }}
</template>

<script setup lang="ts">
import { ref, computed, watch, onServerPrefetch, type PropType } from 'vue'
import { useHead } from '@unhead/vue'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useRogue } from '@/js/services/Rogue'
import type { Category } from '@/js/services/RogueTypes'

interface CategoryLink {
  name: string
  url?: string
  categoryId?: number
}

const props = defineProps({
  links: {
    type    : Array as PropType<CategoryLink[]>,
    default : () => [],
  },
  categoryId: {
    type    : Number,
    default : null,
  },
  breadcrumbColor: {
    type    : String,
    default : null,
  },
})

const rogue = useRogue()
const categoryStore = useCategoryStore()

const ldJSON = ref<Record<string, unknown> | null>(null)
const categories = computed<{[key: string]: Category}>(() => categoryStore.categories)

const category = computed<Category | false>(() => props.categoryId ? categories.value[props.categoryId] : false)
const crumbs = ref<CategoryLink[]>(
  props.categoryId && category.value
    ? [...category.value.path, category.value, ...props.links]
    : props.links
)

const load = async (): Promise<void> => {
  if (props.categoryId !== null && !category.value) {
    await categoryStore.load(props.categoryId, rogue)
  }

  const builtCrumbs: CategoryLink[] = category.value
    ? [...category.value.path, category.value, ...props.links]
    : props.links

  crumbs.value = builtCrumbs

  ldJSON.value = {
    '@context'      : 'https://schema.org',
    '@type'         : 'BreadcrumbList',
    itemListElement : [
      {
        '@type'  : 'ListItem',
        position : 1,
        name     : rogue.site.name,
        item     : rogue.localizedBaseURL,
      },
      ...builtCrumbs
        .filter(c => c.url)
        .map((crumb, i) => ({
          '@type'  : 'ListItem',
          position : i + 2,
          name     : crumb.name,
          item     : `${rogue.localizedBaseURL}${crumb.url}`,
        })),
    ],
  }
}

onServerPrefetch(load)

if (!rogue.ssr) {
  load()
}

watch(() => props.categoryId, load)
watch(() => props.links, load)

useHead({
  script: computed(() => [
    {
      type      : 'application/ld+json',
      key       : 'breadcrumb-ld-json',
      innerHTML : JSON.stringify(ldJSON.value),
    },
  ]),
})
</script>

<style lang="scss" scoped>
.breadcrumbs {
  font-size: $size-xxs;
  margin-bottom: $space-s;
  white-space: nowrap;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  .crumb {
    display: inline-block;
  }

  @media(min-width: $desktop) {
    margin-bottom: $space-m;
  }
}

.breadcrumbs::-webkit-scrollbar {
  display: none;
}

.crumb-link {
  text-decoration: none;

  &, &:active, &:visited {
    color: $color-black-light;
  }

  &:hover {
    color: $color-gray-dark !important;
  }
}

.slash {
  display: inline-block;
  margin-left: $space-xxxs;
  margin-right: $space-xxxs;
}
</style>
